import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Grid,
  Switch,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  CloudUpload as UploadIcon,
  InsertDriveFile as FileIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Description as DocumentIcon,
  OpenInNew as OpenInNewIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import {
  researchProjectService,
  researcherService,
  ndaTypeService,
  ndaService,
  projectInvitationService,
  documentService,
  buildResearchImageUrl,
  buildFileUrl,
} from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import ImageViewer from "../../components/ImageViewer";
import { useNavigate } from "react-router-dom";

const ResearchProjectManagement = () => {
  const { isEstudiante, isInvestigador, isAdmin, isStudent } = useAuth();
  const navigate = useNavigate();
  const canCreate = isInvestigador() || isAdmin(); // Solo investigadores y admins pueden crear
  const [projects, setProjects] = useState([]);
  const [researchers, setResearchers] = useState([]);
  const [ndaTypes, setNdaTypes] = useState([]);
  const [availableNdas, setAvailableNdas] = useState([]);
  const [allNdas, setAllNdas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // State para ImageViewer
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImageSrc, setSelectedImageSrc] = useState("");
  const [selectedImageTitle, setSelectedImageTitle] = useState("");

  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openInviteDialog, setOpenInviteDialog] = useState(false);
  const [openComingSoonDialog, setOpenComingSoonDialog] = useState(false);
  const [openDocumentsDialog, setOpenDocumentsDialog] = useState(false);
  const [approvedDocuments, setApprovedDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [selectedProjectForDocuments, setSelectedProjectForDocuments] =
    useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [viewingProject, setViewingProject] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileVisibility, setFileVisibility] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    status: "draft",
    startDate: "",
    endDate: "",
    keywords: "",
    requiresNDA: true,
    ndaTypeId: "",
    ndaId: "",
  });

  const statusTypes = {
    draft: "Borrador",
    active: "Activo",
    archived: "Archivado",
  };

  const statusColors = {
    draft: "default",
    active: "success",
    archived: "error",
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [projectsRes, ndaTypesRes, ndasRes] = await Promise.all([
        researchProjectService.getAll(),
        ndaTypeService.getAll({ isActive: true }),
        ndaService.getAll({ forProjectAssociation: "true" }),
      ]);
      setProjects(projectsRes.data);
      setNdaTypes(ndaTypesRes.data.data || []);
      // El backend ya filtra los NDAs sin proyecto cuando forProjectAssociation=true
      const allNdas = ndasRes.data || [];
      setAllNdas(allNdas);
      setAvailableNdas(allNdas);
    } catch (err) {
      setError("Error al cargar datos");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = async (project = null) => {
    if (project) {
      try {
        // Cargar el proyecto completo con archivos
        const response = await researchProjectService.getById(project.id);
        const fullProject = response.data;

        setEditingProject(fullProject);

        // Buscar el NDA asociado a este proyecto
        let associatedNdaId = "";
        if (fullProject.id) {
          const ndasRes = await ndaService.getAll();
          const associatedNda = (ndasRes.data || []).find(
            (nda) => nda.researchProjectId === fullProject.id
          );
          if (associatedNda) {
            associatedNdaId = associatedNda.id.toString();
          }
        }

        setFormData({
          title: fullProject.title,
          description: fullProject.description || "",
          status: fullProject.status || "draft",
          startDate: fullProject.startDate
            ? fullProject.startDate.split("T")[0]
            : "",
          endDate: fullProject.endDate ? fullProject.endDate.split("T")[0] : "",
          keywords: fullProject.keywords || "",
          requiresNDA: fullProject.requiresNDA,
          ndaTypeId: fullProject.ndaTypeId || "",
          ndaId: associatedNdaId,
        });

        // Cargar archivos existentes como objetos File simulados para mostrarlos
        const existingFiles = fullProject.files || [];
        setSelectedFiles(
          existingFiles.map((file) => ({
            ...file,
            name: file.originalName,
            isExisting: true, // Marcar como archivo existente
            id: file.id,
          }))
        );

        // Inicializar visibilidad de archivos existentes
        const visibility = {};
        existingFiles.forEach((file, index) => {
          visibility[index] = file.isPublic || false;
        });
        setFileVisibility(visibility);
      } catch (err) {
        console.error("Error loading project:", err);
        setError("Error al cargar el proyecto");
        return;
      }
    } else {
      setEditingProject(null);
      setFormData({
        title: "",
        description: "",
        status: "draft",
        startDate: "",
        endDate: "",
        keywords: "",
        requiresNDA: true,
        ndaTypeId: "",
        ndaId: "",
      });
      setSelectedFiles([]);
      setFileVisibility({});
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingProject(null);
    setSelectedFiles([]);
    setFileVisibility({});
    setError("");
    setSuccess("");
  };

  const handleInputChange = (e) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value,
    });
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    // Agregar nuevos archivos a los existentes (no reemplazar)
    setSelectedFiles((prevFiles) => {
      const existingFiles = prevFiles.filter((f) => f.isExisting);
      const allFiles = [...existingFiles, ...newFiles];

      // Inicializar visibilidad solo para los nuevos archivos
      const existingCount = existingFiles.length;
      const newVisibility = { ...fileVisibility };
      newFiles.forEach((file, index) => {
        newVisibility[existingCount + index] = false; // Por defecto privados
      });
      setFileVisibility(newVisibility);

      return allFiles;
    });
  };

  const handleFileVisibilityChange = (index, isPublic) => {
    setFileVisibility({
      ...fileVisibility,
      [index]: isPublic,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccess("");

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title);
      formDataToSend.append("description", formData.description);
      formDataToSend.append("status", formData.status);
      formDataToSend.append("startDate", formData.startDate);
      formDataToSend.append("endDate", formData.endDate);
      formDataToSend.append("keywords", formData.keywords);
      // Enviar requiresNDA como string para FormData
      formDataToSend.append(
        "requiresNDA",
        formData.requiresNDA ? "true" : "false"
      );
      // Si requiere NDA, debe tener un NDA asociado
      if (formData.requiresNDA) {
        if (!formData.ndaId) {
          setError(
            "Debe seleccionar un NDA existente para asociarlo al proyecto"
          );
          setSubmitting(false);
          return;
        }
        formDataToSend.append("ndaId", formData.ndaId);
      }

      // Append only new files (not existing ones)
      const newFiles = selectedFiles.filter((file) => !file.isExisting);
      newFiles.forEach((file) => {
        formDataToSend.append("files", file);
      });

      // Append file visibility settings
      Object.keys(fileVisibility).forEach((index) => {
        formDataToSend.append(
          `fileVisibility[${index}]`,
          fileVisibility[index]
        );
      });

      if (editingProject) {
        await researchProjectService.update(editingProject.id, formDataToSend);
        setSuccess("Proyecto de investigación actualizado exitosamente");
      } else {
        await researchProjectService.create(formDataToSend);
        setSuccess("Proyecto de investigación creado exitosamente");
      }

      setTimeout(() => {
        handleCloseDialog();
        loadData();
      }, 1500);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Error al guardar proyecto de investigación"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Estás seguro de archivar este proyecto?")) return;

    try {
      await researchProjectService.delete(id);
      setSuccess("Proyecto archivado exitosamente");
      loadData();
    } catch (err) {
      setError("Error al archivar proyecto");
    }
  };

  const handleView = async (id) => {
    try {
      const response = await researchProjectService.getById(id);
      setViewingProject(response.data);
      setOpenViewDialog(true);
    } catch (err) {
      setError("Error al cargar detalles del proyecto");
    }
  };

  const handleRegisterBlockchain = async (projectId) => {
    if (!window.confirm("¿Deseas registrar este proyecto en la blockchain?")) {
      return;
    }

    try {
      setLoading(true);
      setError("");
      await researchProjectService.registerBlockchain(projectId);
      setSuccess("Proyecto registrado en blockchain exitosamente");
      loadData();
    } catch (err) {
      setError(
        err.response?.data?.error || "Error al registrar proyecto en blockchain"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpenInviteDialog = (project) => {
    setViewingProject(project);
    setInviteEmail("");
    setOpenInviteDialog(true);
  };

  const handleSendInvitation = async () => {
    if (!inviteEmail.trim()) {
      setError("Por favor ingresa un email");
      return;
    }

    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail.trim())) {
      setError("Por favor ingresa un email válido");
      return;
    }

    // Mostrar modal informativo en lugar de enviar realmente
    setOpenInviteDialog(false);
    setOpenComingSoonDialog(true);
    setInviteEmail("");
  };

  const handleViewApprovedDocuments = async (project) => {
    try {
      setSelectedProjectForDocuments(project);
      setLoadingDocuments(true);
      setError("");
      setOpenDocumentsDialog(true);

      // Obtener todos los documentos del proyecto
      const response = await documentService.getAll({ projectId: project.id });
      const allDocuments = response.data || [];

      // Filtrar solo documentos aprobados (reviewStatus = 1) de colaboradores (role = "collaborator")
      const approvedCollaboratorDocs = allDocuments.filter((doc) => {
        return (
          doc.reviewStatus === 1 && // Aprobado
          doc.User?.role === "collaborator" // De un colaborador
        );
      });

      setApprovedDocuments(approvedCollaboratorDocs);
    } catch (err) {
      console.error("Error loading approved documents:", err);
      setError("Error al cargar los documentos aprobados");
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleCloseDocumentsDialog = () => {
    setOpenDocumentsDialog(false);
    setApprovedDocuments([]);
    setSelectedProjectForDocuments(null);
    setError("");
  };

  const handleDownloadDocument = async (doc) => {
    try {
      setError("");
      const response = await documentService.download(doc.id);

      // Crear blob y descargar
      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data]);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Obtener la extensión del archivo del storagePath o título
      const ext = doc.storagePath ? doc.storagePath.split(".").pop() : "";
      const filename = ext ? `${doc.title}.${ext}` : doc.title;

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSuccess("Documento descargado exitosamente");
    } catch (err) {
      console.error("Error downloading document:", err);
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.details ||
        err.response?.data?.message ||
        err.message ||
        "Error al descargar el documento";
      setError(errorMessage);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h4" component="h1">
              Gestión de Proyectos de Investigación
            </Typography>
            {canCreate && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                Nuevo Proyecto
              </Button>
            )}
          </Box>
        </Paper>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess("")}
          >
            {success}
          </Alert>
        )}

        {/* Projects Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Título</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Archivos</TableCell>
                <TableCell>Visibilidad</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projects.map((project) => (
                <TableRow key={project.id} hover>
                  <TableCell
                    onClick={() => handleView(project.id)}
                    sx={{ cursor: "pointer" }}
                  >
                    <Typography variant="subtitle2">{project.title}</Typography>
                  </TableCell>
                  <TableCell
                    onClick={() => handleView(project.id)}
                    sx={{ cursor: "pointer" }}
                  >
                    <Chip
                      label={statusTypes[project.status]}
                      color={statusColors[project.status]}
                      size="small"
                    />
                  </TableCell>
                  <TableCell
                    onClick={() => handleView(project.id)}
                    sx={{ cursor: "pointer" }}
                  >
                    {project.files && project.files.length > 0 ? (
                      <Chip
                        icon={<FileIcon />}
                        label={`${project.files.length} archivo(s)`}
                        size="small"
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        Sin archivos
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell
                    onClick={() => handleView(project.id)}
                    sx={{ cursor: "pointer" }}
                  >
                    <Chip
                      label={project.isPublic ? "Público" : "Privado"}
                      color={project.isPublic ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleView(project.id);
                      }}
                      title="Ver"
                      sx={{ color: "#1976d2" }}
                    >
                      <ViewIcon />
                    </IconButton>
                    {(isInvestigador() || isAdmin()) && (
                      <Tooltip title="Ver Documentos">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(
                              `/research-projects/${project.id}/documents`
                            );
                          }}
                          sx={{ color: "#f57c00" }}
                        >
                          <DocumentIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {isStudent() && (
                      <Tooltip title="Ver Documentos">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewApprovedDocuments(project);
                          }}
                          sx={{ color: "#f57c00" }}
                        >
                          <DocumentIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {!project.blockchainTxHash && (
                      <Tooltip title="Registrar en Blockchain">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRegisterBlockchain(project.id);
                          }}
                          sx={{ color: "#9c27b0" }}
                        >
                          <UploadIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canCreate && (
                      <>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDialog(project);
                          }}
                          title="Editar"
                          sx={{ color: "#2e7d32" }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(project.id);
                          }}
                          title="Archivar"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {projects.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No hay proyectos de investigación registrados
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Create/Edit Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {editingProject
              ? "Editar Proyecto de Investigación"
              : "Nuevo Proyecto de Investigación"}
          </DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="title"
                    label="Título del Proyecto *"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="description"
                    label="Descripción"
                    multiline
                    rows={3}
                    value={formData.description}
                    onChange={handleInputChange}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Estado</InputLabel>
                    <Select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      label="Estado"
                    >
                      {Object.entries(statusTypes).map(([key, label]) => (
                        <MenuItem key={key} value={key}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="startDate"
                    label="Fecha de Inicio"
                    type="date"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="endDate"
                    label="Fecha de Fin"
                    type="date"
                    value={formData.endDate}
                    onChange={handleInputChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="keywords"
                    label="Palabras Clave"
                    value={formData.keywords}
                    onChange={handleInputChange}
                    placeholder="Palabras clave separadas por comas"
                    helperText="Separa las palabras clave con comas para mejor búsqueda"
                  />
                </Grid>

                {/* Removed: Publicly searchable toggle */}
                {/*
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="isPublic"
                        checked={formData.isPublic}
                        onChange={handleInputChange}
                      />
                    }
                    label="Hacer este proyecto públicamente buscable"
                  />
                </Grid>
                */}

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="requiresNDA"
                        checked={formData.requiresNDA}
                        onChange={handleInputChange}
                      />
                    }
                    label="Requerir NDA para acceso"
                  />
                </Grid>

                {formData.requiresNDA && (
                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel>Asociar NDA Existente</InputLabel>
                      <Select
                        name="ndaId"
                        value={formData.ndaId}
                        onChange={handleInputChange}
                        label="Asociar NDA Existente"
                      >
                        <MenuItem value="">Seleccione un NDA</MenuItem>
                        {Array.isArray(availableNdas) &&
                          availableNdas.length > 0 &&
                          availableNdas.map((nda) => (
                            <MenuItem key={nda.id} value={nda.id}>
                              {nda.isGeneric ? "[Genérico] " : ""}{nda.title}{" "}
                              {nda.description
                                ? `- ${nda.description.substring(0, 30)}...`
                                : ""}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ mt: 0.5, display: "block" }}
                    >
                      Seleccione un NDA existente para asociarlo a este proyecto
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<UploadIcon />}
                      fullWidth
                    >
                      Cargar Archivos
                      <input
                        type="file"
                        hidden
                        multiple
                        onChange={handleFileChange}
                        accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.xls,.xlsx,.ppt,.pptx,.zip,.rar"
                      />
                    </Button>
                    {selectedFiles.length > 0 && (
                      <List dense>
                        {selectedFiles.map((file, index) => (
                          <ListItem key={index}>
                            <ListItemIcon>
                              <FileIcon />
                            </ListItemIcon>
                            <ListItemText
                              primary={file.name || file.originalName}
                              secondary={
                                file.isExisting
                                  ? "Archivo existente"
                                  : `${(file.size / 1024 / 1024).toFixed(2)} MB`
                              }
                            />
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={
                                    formData.requiresNDA
                                      ? fileVisibility[index] || false
                                      : true
                                  }
                                  onChange={(e) =>
                                    handleFileVisibilityChange(
                                      index,
                                      e.target.checked
                                    )
                                  }
                                  size="small"
                                  disabled={!formData.requiresNDA}
                                />
                              }
                              label={
                                formData.requiresNDA
                                  ? fileVisibility[index]
                                    ? "Público"
                                    : "Privado"
                                  : "Público"
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                      sx={{ mt: 1 }}
                    >
                      {formData.requiresNDA
                        ? "Podés marcar archivos como Privados o Públicos."
                        : "Este proyecto no requiere NDA, por lo tanto todos los archivos serán Públicos."}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={submitting}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} /> : null}
            >
              {submitting
                ? editingProject
                  ? "Actualizando..."
                  : "Creando..."
                : editingProject
                ? "Actualizar"
                : "Crear"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Dialog */}
        <Dialog
          open={openViewDialog}
          onClose={() => setOpenViewDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Detalles del Proyecto</DialogTitle>
          <DialogContent>
            {viewingProject && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {viewingProject.title}
                </Typography>

                {viewingProject.description && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">Descripción:</Typography>
                    <Typography variant="body2" paragraph>
                      {viewingProject.description}
                    </Typography>
                  </Box>
                )}

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">Estado:</Typography>
                    <Chip
                      label={statusTypes[viewingProject.status]}
                      color={statusColors[viewingProject.status]}
                      size="small"
                    />
                  </Grid>

                  {viewingProject.keywords && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2">
                        Palabras Clave:
                      </Typography>
                      <Typography variant="body2">
                        {viewingProject.keywords}
                      </Typography>
                    </Grid>
                  )}

                  {viewingProject.files &&
                    Array.isArray(viewingProject.files) &&
                    viewingProject.files.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>
                          Archivos Adjuntos:
                        </Typography>
                        <List dense>
                          {viewingProject.files.map((file, index) => (
                            <ListItem key={index}>
                              <ListItemIcon>
                                {file.isPublic ? (
                                  <LockOpenIcon color="success" />
                                ) : (
                                  <LockIcon color="warning" />
                                )}
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Typography variant="body2">
                                      {file.normalizedName}
                                    </Typography>
                                    <Chip
                                      label={
                                        file.isPublic ? "Público" : "Privado"
                                      }
                                      size="small"
                                      color={
                                        file.isPublic ? "success" : "warning"
                                      }
                                      variant="outlined"
                                    />
                                  </Box>
                                }
                                secondary={file.originalName}
                              />
                              <ListItemSecondaryAction>
                                {file.path && (
                                  <IconButton
                                    edge="end"
                                    size="small"
                                    onClick={() => {
                                      const imageUrl = buildResearchImageUrl(
                                        file.path
                                      );
                                      setSelectedImageSrc(imageUrl);
                                      setSelectedImageTitle(
                                        file.normalizedName || file.originalName
                                      );
                                      setImageViewerOpen(true);
                                    }}
                                    title="Ver archivo"
                                  >
                                    <ViewIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </ListItemSecondaryAction>
                            </ListItem>
                          ))}
                        </List>
                      </Grid>
                    )}

                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">Visibilidad:</Typography>
                    <Chip
                      label={viewingProject.isPublic ? "Público" : "Privado"}
                      color={viewingProject.isPublic ? "success" : "default"}
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">NDA Requerido:</Typography>
                    <Chip
                      label={viewingProject.requiresNDA ? "Sí" : "No"}
                      color={viewingProject.requiresNDA ? "warning" : "default"}
                      size="small"
                    />
                  </Grid>

                  {viewingProject.blockchainTxHash && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom>
                        Blockchain Transaction Hash:
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "monospace",
                            fontSize: "0.875rem",
                            wordBreak: "break-all",
                          }}
                        >
                          {viewingProject.blockchainTxHash}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<OpenInNewIcon />}
                          href={`https://polygonscan.com/tx/${viewingProject.blockchainTxHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ flexShrink: 0 }}
                        >
                          Ver en PolygonScan
                        </Button>
                      </Box>
                    </Grid>
                  )}

                  {(() => {
                    // Buscar el NDA asociado al proyecto
                    const associatedNda = allNdas.find(
                      (nda) => nda.researchProjectId === viewingProject.id
                    );
                    return associatedNda ? (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2">
                          NDA Asociado:
                        </Typography>
                        <Typography variant="body2">
                          {associatedNda.title}
                          {associatedNda.description &&
                            ` - ${associatedNda.description}`}
                        </Typography>
                      </Grid>
                    ) : null;
                  })()}

                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary">
                      Creado:{" "}
                      {new Date(viewingProject.createdAt).toLocaleString()}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            {viewingProject?.requiresNDA && (isInvestigador() || isAdmin()) && (
              <Button
                variant="outlined"
                onClick={() => handleOpenInviteDialog(viewingProject)}
              >
                Invitar por email
              </Button>
            )}
            <Button onClick={() => setOpenViewDialog(false)}>Cerrar</Button>
          </DialogActions>
        </Dialog>

        {/* Image Viewer */}
        <ImageViewer
          open={imageViewerOpen}
          onClose={() => setImageViewerOpen(false)}
          imageSrc={selectedImageSrc}
          imageTitle={selectedImageTitle}
        />

        {/* Invite Dialog */}
        <Dialog
          open={openInviteDialog}
          onClose={() => setOpenInviteDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Invitar usuario al proyecto</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Ingresa el email del usuario que deseas invitar al proyecto{" "}
              <strong>{viewingProject?.title}</strong>
            </Typography>
            <TextField
              fullWidth
              label="Email del usuario"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="ejemplo@email.com"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenInviteDialog(false)}>Cancelar</Button>
            <Button
              onClick={handleSendInvitation}
              variant="contained"
              disabled={submitting || !inviteEmail.trim()}
            >
              {submitting ? "Enviando..." : "Enviar Invitación"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Coming Soon Dialog */}
        <Dialog
          open={openComingSoonDialog}
          onClose={() => setOpenComingSoonDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Funcionalidad Próximamente</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Invitación por Email:</strong> Esta funcionalidad estará
                disponible en el futuro. Podrás invitar usuarios por email para
                que participen en tus proyectos de investigación.
              </Typography>
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setOpenComingSoonDialog(false)}
              variant="contained"
            >
              Entendido
            </Button>
          </DialogActions>
        </Dialog>

        {/* Documents Dialog - For Students to view approved collaborator documents */}
        <Dialog
          open={openDocumentsDialog}
          onClose={handleCloseDocumentsDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Documentos Aprobados de Colaboradores
            {selectedProjectForDocuments && (
              <Typography
                variant="caption"
                display="block"
                color="textSecondary"
              >
                Proyecto: {selectedProjectForDocuments.title}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            {loadingDocuments ? (
              <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
                <CircularProgress />
              </Box>
            ) : approvedDocuments.length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                No hay documentos aprobados de colaboradores para este proyecto.
              </Alert>
            ) : (
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Título</TableCell>
                      <TableCell>Colaborador</TableCell>
                      <TableCell>Fecha</TableCell>
                      <TableCell align="right">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {approvedDocuments.map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell>
                          <Typography variant="body2">{doc.title}</Typography>
                          {doc.description && (
                            <Typography variant="caption" color="textSecondary">
                              {doc.description}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {doc.User?.name || "Colaborador desconocido"}
                        </TableCell>
                        <TableCell>
                          {new Date(doc.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Descargar">
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadDocument(doc)}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDocumentsDialog} variant="contained">
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default ResearchProjectManagement;
